import { Badge, Group, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core'
import { IconReceipt2 } from '@tabler/icons-react'
import dayjs from 'dayjs'

import type { AdminRedeemable } from '@repo/common/queries/admin/redeem'

import { IconRedeem } from '@/components/icons/IconRedeem.tsx'
import { BlockchainAddress } from '@/components/ui/BlockchainAddress'
import { TowerCard } from '@/components/ui/TowerCard'

interface Props {
  redeemable: AdminRedeemable | undefined
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {children}
    </Text>
  </Stack>
)

export const RedeemableDetail: React.FC<Props> = ({ redeemable }) => {
  if (!redeemable) {
    return (
      <TowerCard title="Redeemable" icon={<IconReceipt2 size={24} />}>
        <Stack gap="md">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={20} width="60%" />
          ))}
        </Stack>
      </TowerCard>
    )
  }

  return (
    <TowerCard
      title={redeemable.name}
      description={`Redeemable ID: ${redeemable.id}`}
      icon={<IconRedeem size={24} />}
      extraHeader={
        <Group gap="xs">
          {redeemable.published && (
            <Badge variant="light" color="green" size="sm">
              Published
            </Badge>
          )}
          {!redeemable.published && (
            <Badge variant="light" color="gray" size="sm">
              Unpublished
            </Badge>
          )}
          {!redeemable.paused && (
            <Badge variant="light" color="green" size="sm">
              Active
            </Badge>
          )}
          {redeemable.paused && (
            <Badge variant="light" color="orange" size="sm">
              Paused
            </Badge>
          )}
          {/*{redeemable.unmatched_reversals_paused && (*/}
          {/*  <Badge variant="light" color="orange" size="sm">*/}
          {/*    Reversals Paused*/}
          {/*  </Badge>*/}
          {/*)}*/}
        </Group>
      }
    >
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg">
        <Field label="Commission Fee Schedule">
          {redeemable.commission_fee_schedule}
        </Field>
        <Field label="Underlying Schedule">
          {redeemable.underlying_schedule}
        </Field>
        <Field label="Price Feed Protocol">
          {redeemable.price_feed_protocol}
        </Field>
        <Field label="Price Feed Network">
          {redeemable.price_feed_network ?? 'N/A'}
        </Field>
        <Field label="Price Feed Address">
          {redeemable.price_feed_address ? (
            <BlockchainAddress
              address={redeemable.price_feed_address}
              network={redeemable.price_feed_network}
            />
          ) : (
            'N/A'
          )}
        </Field>
        <Field label="Rate Adjustment">
          {redeemable.rate_adjustment != null
            ? redeemable.rate_adjustment
            : 'None'}
        </Field>
        <Field label="Escrow Vault ID">
          {redeemable.fireblocks_escrow_vault_id ?? 'N/A'}
        </Field>
        <Field label="Holdings Vault ID">
          {redeemable.fireblocks_holdings_vault_id ?? 'N/A'}
        </Field>
        <Field label="Fee Recovery Asset">
          {redeemable.fee_recovery_asset ?? 'N/A'}
        </Field>
        <Field label="Fees Recovery Vault ID">
          {redeemable.fees_recovery_vault_id ?? 'N/A'}
        </Field>
        <Field label="Total Redemptions">{redeemable.total_redemptions}</Field>
        <Field label="In Flight">{redeemable.in_flight_redemptions}</Field>
        <Field label="Last 24h">{redeemable.redemptions_last_24h}</Field>
        <Field label="Non-Success 24h">
          {redeemable.non_success_redemptions_last_24h}
        </Field>
        <Field label="Created">
          {dayjs(redeemable.created_at).format('MMM D, YYYY h:mm A')}
        </Field>
        <Field label="Updated">
          {dayjs(redeemable.updated_at).format('MMM D, YYYY h:mm A')}
        </Field>
      </SimpleGrid>
    </TowerCard>
  )
}
